// TODO - remove lint disable when editing this file
/* eslint-disable */
import {
  EditorState,
  convertFromHTML,
  SelectionState,
  Modifier,
  CharacterMetadata,
} from 'draft-js'
import { Map } from 'immutable'
import {
  getEndOfTextSelection,
  insertContentBlock,
  insertEllpsisBlock,
  insertEmptyBlock,
  InsertTypes,
} from './draftUtils'
import { BEFORE_FROM_HTML, AFTER_FROM_HTML } from './Factory'

const DEFAULT_SANITIZE_OPTIONS = {
  keepTrailingSoftbreaks: false,
  keepBodySoftbreaks: false,
}

export const _replaceMatchedText = (aBlock, regex, replacement) => {
  // Get first matching substring and delete both text and characterList array
  while (true) {
    const text = aBlock.getText()
    regex.lastIndex = 0
    const match = regex.exec(text)
    if (!match) {
      break
    }
    const index = match.index
    const length = match[0].length
    const characterMetadata = Array(replacement.length).fill(
      new CharacterMetadata()
    )
    aBlock = aBlock.merge({
      text:
        text.substring(0, index) + replacement + text.substring(index + length),
      characterList: aBlock
        .getCharacterList()
        .splice(index, length, ...characterMetadata),
    })
  }
  return aBlock
}

const REGEX_TRAILING_SOFTBREAKS = /\n+$/g
const REGEX_ANY_SOFTBREAKS = /\n+/g

export const _sanitizeSoftBreaks = (options) => (aBlock) => {
  const text = aBlock.getText()
  // Do a fast check for soft breaks
  if (!~text.indexOf('\n')) {
    return aBlock
  }
  // Firstly, trim out all trailing soft breaks
  aBlock = options.keepTrailingSoftbreaks
    ? aBlock
    : _replaceMatchedText(aBlock, REGEX_TRAILING_SOFTBREAKS, '')
  // Replace any occurences of softbreaks to spaces
  aBlock = options.keepBodySoftbreaks
    ? aBlock
    : _replaceMatchedText(aBlock, REGEX_ANY_SOFTBREAKS, ' ')
  return aBlock
}

export default class ConverterContext {
  constructor(factory) {
    this.draftContent = Map({
      contentState: EditorState.createEmpty().getCurrentContent(),
      customStyleMap: Map(),
    })
    this._factory = factory
    this._hasAppended = false
  }

  getAppendSelection() {
    const contentState = this.draftContent.get('contentState')
    return this._hasAppended ? getEndOfTextSelection(contentState) : null
  }

  getInsertType() {
    // EditorState.createEmpty create one unstyled block, so use at insert type initially
    return this._hasAppended ? InsertTypes.AFTER : InsertTypes.AT
  }

  appendBlock(block) {
    const contentState = this.draftContent.get('contentState')
    const selection = this.getAppendSelection()

    const updatedState = insertContentBlock(
      contentState,
      selection,
      block,
      this.getInsertType()
    )
    this.draftContent = this.draftContent.set(
      'contentState',
      updatedState.contentState
    )
    this._hasAppended = true
    return updatedState.contentBlocks
  }

  appendEmptyBlock(type) {
    const contentState = this.draftContent.get('contentState')
    const selection = this.getAppendSelection()
    const updatedState = insertEmptyBlock(
      contentState,
      selection,
      this.getInsertType(),
      type
    )

    this.draftContent = this.draftContent.set(
      'contentState',
      updatedState.contentState
    )
    this._hasAppended = true
    return updatedState.contentBlocks
  }

  appendAtomicBlock(props) {
    const contentState = this.draftContent.get('contentState')
    const selection = this.getAppendSelection()

    const updatedState = insertEllpsisBlock(
      contentState,
      selection,
      props,
      this.getInsertType()
    )

    this.draftContent = this.draftContent.set(
      'contentState',
      updatedState.contentState
    )
    this._hasAppended = true
    return updatedState.contentBlocks
  }

  generateDraftContent(emptyBlock) {
    const contentState = this.draftContent.get('contentState')
    const selection = this.getAppendSelection()

    if (emptyBlock) {
      // Insert empty block at the end to easily adding new content and prevent atomic
      // block spreading if it's a last block in content
      this.draftContent = this.draftContent.set(
        'contentState',
        insertEmptyBlock(
          contentState,
          selection,
          this.getInsertType(),
          'unstyled',
          Map({ isEmpty: true })
        ).contentState
      )
    }
    return this.draftContent
  }

  replaceBlocks(blocks = []) {
    const contentState = this.draftContent.get('contentState')
    let blockMap = contentState.getBlockMap()
    blocks.forEach((block) => {
      blockMap = blockMap.set(block.getKey(), block)
    })
    this.draftContent = this.draftContent.set(
      'contentState',
      contentState.set('blockMap', blockMap)
    )
  }

  mergeCustomStyleMap(customStyleMap) {
    this.draftContent = this.draftContent.set(
      'customStyleMap',
      this.draftContent.get('customStyleMap').merge(customStyleMap)
    )
  }

  setAnsElementMetaData(blockKey, elementMetaData) {
    const contentState = Modifier.mergeBlockData(
      this.draftContent.get('contentState'),
      SelectionState.createEmpty(blockKey),
      elementMetaData
    )
    this.draftContent = this.draftContent.set('contentState', contentState)
    return contentState.getBlockForKey(blockKey)
  }

  toDraftBlock(html, ans, defaultBlocks, options) {
    this._factory &&
      this._factory.getMiddlewares(BEFORE_FROM_HTML).forEach((middleware) => {
        html = middleware(html, ans, options)
      })

    let blocksFromHTML = convertFromHTML(html) || {}
    if (!blocksFromHTML.contentBlocks || !blocksFromHTML.contentBlocks.length) {
      blocksFromHTML.contentBlocks = defaultBlocks || null
    }

    blocksFromHTML.contentBlocks =
      blocksFromHTML.contentBlocks &&
      blocksFromHTML.contentBlocks.map(
        _sanitizeSoftBreaks(options || DEFAULT_SANITIZE_OPTIONS)
      )

    this._factory &&
      this._factory.getMiddlewares(AFTER_FROM_HTML).forEach((middleware) => {
        blocksFromHTML = middleware(blocksFromHTML, ans)
      })

    return {
      contentBlocks: blocksFromHTML.contentBlocks,
      entityMap: blocksFromHTML.entityMap,
    }
  }
}
